import React, { useEffect, useState, useCallback } from 'react';
import { makeStyles } from 'design-system';
import Dialog from 'react-scale/components/design-system/Dialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AttachmentInstructionJson } from '@scale/shared/projectInstruction/types';
import { AttachmentInstructionType } from '@scale/shared/defaults';
import API from '@scale/frontend-lib/dashboard-api';
import { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import { useProjectInstructionContext } from '../projectInstructionContext';

const Attachment = ({ contentData }: { contentData: AttachmentInstructionJson }) => {
  const classes = useStyles();
  const { isMobileView } = useProjectInstructionContext();
  const { description, title, attachmentType, attachmentUrl } = contentData;
  const [assetUrl, setAssetUrl] = useState<string>('');
  const [open, setOpen] = useState<boolean>(isMobileView);

  useEffect(() => {
    if (
      attachmentType === AttachmentInstructionType.Image ||
      attachmentType === AttachmentInstructionType.Pdf
    ) {
      (async () => {
        try {
          const assetId = description;
          const asset = await API.selfserve.fetchInstructionAssetS3URL({ assetId });
          if (asset && asset.signedURL) {
            setAssetUrl(asset.signedURL);
          }
        } catch (e) {
          defaultApiFetchErrorHandler(e, { handleHttp: true });
        }
      })();
    }
  }, [attachmentType, description]);

  const Content = useCallback(
    ({ actionButton }: { actionButton?: JSX.Element }) => (
      <>
        <div className={classes.titleContainer}>
          <h3 className={classes.title}>
            {title}
            {actionButton}
          </h3>
          {contentData.hiddenFromCustomer && (
            <FontAwesomeIcon className={classes.hideIcon} icon="eye-slash" />
          )}
        </div>

        {attachmentType === AttachmentInstructionType.Iframe && (
          <div className={classes.iframeContainer}>
            <iframe src={attachmentUrl} frameBorder="0" className={classes.iframe} />
          </div>
        )}

        {attachmentType === AttachmentInstructionType.Image && (
          <img style={{ maxHeight: 600 }} src={assetUrl} />
        )}

        {attachmentType === AttachmentInstructionType.Pdf && (
          <iframe src={assetUrl} width="100%" height="600" />
        )}
      </>
    ),
    [attachmentUrl, assetUrl],
  );

  if (isMobileView) {
    return (
      <div className={classes.container}>
        <Content
          actionButton={
            <button className={classes.btn} onClick={() => setOpen(true)}>
              <FontAwesomeIcon icon="window-restore" />
            </button>
          }
        />
        <Dialog open={open} className={classes.dialog} onClose={() => setOpen(false)}>
          <Content
            actionButton={
              <button className={classes.btn} onClick={() => setOpen(false)}>
                &#10005;
              </button>
            }
          />
        </Dialog>
      </div>
    );
  }
  return <Content />;
};

const useStyles = makeStyles(theme => ({
  title: {
    fontWeight: 600,
    position: 'relative',
  },
  iframeContainer: {
    position: 'relative',
    height: '90%',
    minHeight: 700,
  },
  iframe: {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
  },

  dialog: {
    '& .MuiPaper-root': {
      margin: '0 !important',
      width: '80vw',
      maxHeight: 'inherit',
      padding: '0 10px',
      overflow: 'scroll',
    },
  },
  container: {
    padding: 10,
    '& > button': {
      position: 'relative',
      left: 130,
    },
  },

  btn: {
    position: 'absolute',
    right: '5%',
    background: 'none',
    color: theme.colors.gray50,
    border: 'none',
    padding: 0,
    font: 'inherit',
    cursor: 'pointer',
    outline: 'inherit',
    frontWeight: 500,
    '&:hover': {
      color: theme.colors.gray75,
    },
  },
  hideIcon: {
    color: theme.colors.gray50,
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

export default Attachment;
